import React from 'react';
import { Input } from 'antd';
import { Form } from 'antd';

interface TelephoneFieldProps {
  value?: string;
  onChange?: (value?: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
  className?: string;
  maxLength?: number;
  status?: 'error' | 'warning';
}

export const TelephoneField: React.FC<TelephoneFieldProps> = ({
  value,
  onChange,
  placeholder = 'Enter phone number',
  disabled = false,
  required = false,
  size = 'large',
  style,
  className,
  maxLength = 10,
  status,
}) => {
  const getSizeClass = () => {
    switch (size) {
      case 'small':
        return 'ant-input-sm';
      case 'large':
        return 'ant-input-lg';
      default:
        return '';
    }
  };

  // Clean the value to remove +91 or 91 prefix if present
  const cleanValue = React.useMemo(() => {
    if (!value) return '';

    // Check if original value starts with +91 or 91 prefix
    const hasCountryCodePrefix = /^(\+91|91)\s*/.test(value);
    
    // First, remove any non-digit characters
    let cleaned = value.replace(/\D/g, '');

    // Only remove +91 or 91 prefix if:
    // 1. The original value started with +91 or 91
    // 2. The cleaned value has 12 or more digits (indicating country code + number)
    // This prevents removing "91" when user is typing "91" as part of a shorter number
    if (hasCountryCodePrefix && cleaned.length >= 12) {
      // Remove country code prefix (91) if present
      cleaned = cleaned.replace(/^91/, '');
    }

    // Limit to maxLength digits
    cleaned = cleaned.slice(0, maxLength);

    return cleaned;
  }, [value, maxLength]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      // Remove any non-digit characters and limit to maxLength digits
      const inputValue = e.target.value;
      const cleanValue = inputValue.replace(/\D/g, '').slice(0, maxLength);
      onChange(cleanValue);
    }
  };

  return (
    <div
      className={`phone-input-wrapper ${getSizeClass()} ${className || ''}`}
      style={{ width: '100%' }}
    >
      <div className="phone-prefix">+91</div>
      <Input
        value={cleanValue}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        size={size}
        status={status}
        className={`phone-input ${getSizeClass()}`}
        style={{
          ...style,
          width: '100%',
          border: 'none',
          borderRadius: 0,
          boxShadow: 'none',
          background: 'transparent',
        }}
        maxLength={maxLength}
      />
    </div>
  );
};

// Form Item wrapper for Ant Design Form integration
export const TelephoneInputFormItem: React.FC<{
  name: string;
  label?: string;
  required?: boolean;
  rules?: any[];
  value?: string;
  onChange?: (value?: string) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
  className?: string;
  maxLength?: number;
}> = ({
  name,
  label,
  required = false,
  rules = [],
  value,
  onChange,
  placeholder,
  disabled,
  size,
  style,
  className,
  maxLength,
}) => {
  const validationRules = [
    ...rules,
    {
      required,
      message: `${label || 'Phone number'} is required`,
    },
    {
      validator: (_: any, value: string) => {
        if (!value) {
          return Promise.resolve();
        }
        // Check if original value starts with +91 or 91 prefix (before cleaning)
        const hasCountryCodePrefix = /^(\+91|91)\s*/.test(value);
        
        // Clean the value to remove any non-digits
        let cleanValue = value.replace(/\D/g, '');
        
        // Handle country code: only remove 91 prefix if value has 12+ digits
        // This matches the logic in cleanValue useMemo
        if (hasCountryCodePrefix && cleanValue.length >= 12) {
          cleanValue = cleanValue.replace(/^91/, '');
        }
        
        // Check if it's exactly 10 digits and starts with 2-9
        if (cleanValue.length !== 10) {
          return Promise.reject(new Error('Please enter a valid 10-digit mobile number'));
        }
        
        if (!/^[2-9]/.test(cleanValue)) {
          return Promise.reject(new Error('Please enter a valid 10-digit mobile number starting with 2-9'));
        }
        
        return Promise.resolve();
      },
    },
  ];

  return (
    <Form.Item name={name} label={label} rules={validationRules} style={style}>
      <TelephoneField
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        size={size}
        className={className}
        maxLength={maxLength}
      />
    </Form.Item>
  );
};
